/**
 * Messages — Split-view DM inbox.
 * Route: /messages and /messages/:conversationId
 *
 * Left column: thread list with search and filter pills.
 * Right column: active conversation (embedded Conversation component).
 */

import { useState, useEffect } from 'react';
import { Loader, Mail, MessageCircle, Search } from 'react-feather';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import MonoAvatar from '../components/MonoAvatar';
import Conversation from './Conversation';
import { getConversations } from '../services/messageService';
import { useMessageRequests } from '../hooks/useMessageRequests';
import MessageRequestSection from '../components/MessageRequestSection';
import type { Conversation as ConversationType } from '../types/social';
import { toDateSafe } from '../utils/dateUtils';
import '../styles/messages.css';

function timeAgo(ts: any): string {
  const d = toDateSafe(ts);
  if (!d) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

type FilterType = 'all' | 'unread' | 'people' | 'circles';

export default function Messages() {
  const { user } = useAuth();
  const { isAuthenticated } = useAuthGate();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navState = location.state as { recipientId?: string; recipientName?: string } | null;

  const [conversations, setConversations] = useState<ConversationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  // PPL-429 — requests waiting on me. This is their durable home: the prompt
  // is shown once, this section stays until each one is answered.
  const { requests } = useMessageRequests();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated) return;
    loadConversations();
  }, [user?.uid]);

  async function loadConversations() {
    try {
      setLoading(true);
      const res = await getConversations(20);
      setConversations(res.conversations);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const lastChat = conversations[conversations.length - 1];
      const res = await getConversations(20, lastChat?.chatId);
      setConversations((prev) => [...prev, ...res.conversations]);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('Error loading more conversations:', err);
    } finally {
      setLoadingMore(false);
    }
  }

  const filtered = conversations.filter((c) => {
    // A request I have not answered lives in the requests section above, with
    // the decision attached. Leaving it here too would show the message twice
    // and let it be read before deciding — the server still returns it so an
    // older build can answer by replying (PPL-428).
    if (c.status === 'requested' && c.requestedBy && c.requestedBy !== user?.uid) return false;
    if (filter === 'unread' && !c.unread) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(c.recipientName || '').toLowerCase().includes(q) &&
          !(c.lastMessage || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const filters: { id: FilterType; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread' },
    { id: 'people', label: 'People' },
    { id: 'circles', label: 'Circles' },
  ];

  // Find the active conversation's info for passing to Conversation component.
  // Fall back to navigation state when the URL param is a fresh recipientId
  // (e.g. arriving from an F8 card before any messages exist between the two).
  const matchedConvo = conversationId
    ? conversations.find(c => c.chatId === conversationId)
    : null;
  const activeConvo = matchedConvo
    || (conversationId && navState?.recipientId
      ? {
          chatId: conversationId,
          recipientId: navState.recipientId,
          recipientName: navState.recipientName,
        } as Partial<ConversationType> as ConversationType
      : null);

  return (
    <div className="feed">
      <SEO title="Messages — pplkpr" description="Your direct messages on pplkpr" path="/messages" />

      <div className={`messages-split${conversationId ? ' has-conversation' : ''}`}>
        {/* ── Left column: thread list ── */}
        <div className="messages-list-panel">
          {/* Header */}
          <div style={{ padding: '8px 16px 14px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}>
              <div>
                <h1 style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}>messages</h1>
                {conversations.length > 0 && (
                  <span style={{
                    fontSize: 12,
                    color: 'rgba(220,200,240,0.5)',
                    marginTop: 2,
                    display: 'block',
                  }}>
                    {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <button title="New message" style={{
                appearance: 'none',
                border: '1px solid rgba(217, 70, 239, 0.4)',
                cursor: 'pointer',
                width: 32,
                height: 32,
                borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(217,70,239,0.25), rgba(124,58,237,0.25))',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5M18.4 2.6a2 2 0 0 1 2.8 2.8L12 14.6 8 16l1.4-4 9-9.4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Search */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 12px',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <Search size={14} color="rgba(220,200,240,0.6)" />
              <input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 0,
                  outline: 'none',
                  color: '#fff',
                  fontSize: 13,
                  fontFamily: 'inherit',
                }}
              />
            </div>
          </div>

          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 4, padding: '0 12px 8px', overflowX: 'auto' }}>
            {filters.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  appearance: 'none',
                  border: filter === f.id
                    ? '1px solid rgba(217, 70, 239, 0.45)'
                    : '1px solid rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: filter === f.id
                    ? 'rgba(217, 70, 239, 0.2)'
                    : 'rgba(255,255,255,0.03)',
                  color: filter === f.id ? '#fff' : 'rgba(220,200,240,0.7)',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.12s',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Thread list */}
          <div style={{ flex: 1, overflow: 'auto', padding: '0 8px 16px' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
                <Loader size={20} className="spin" style={{ color: 'var(--text-dim)' }} />
              </div>
            ) : conversations.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                padding: '3rem 1rem',
                color: 'rgba(220,200,240,0.4)',
                textAlign: 'center',
              }}>
                <Mail size={32} />
                <h3 style={{ margin: 0, color: 'rgba(220,200,240,0.6)', fontSize: 14, fontWeight: 600 }}>no messages yet</h3>
                <p style={{ margin: 0, fontSize: 13 }}>when you start a conversation with someone, it will appear here.</p>
                <Link to="/find-ppl" className="primary-button" style={{ fontSize: 13, marginTop: 4 }}>
                  <MessageCircle size={14} />
                  <span>find people</span>
                </Link>
              </div>
            ) : (
              <>
                <MessageRequestSection requests={requests} />
                {filtered.map((c) => (
                  <button
                    key={c.chatId}
                    onClick={() => navigate(`/messages/${c.chatId}`, {
                      state: { recipientId: c.recipientId, recipientName: c.recipientName },
                    })}
                    style={{
                      appearance: 'none',
                      border: conversationId === c.chatId
                        ? '1px solid rgba(217, 70, 239, 0.3)'
                        : '1px solid transparent',
                      cursor: 'pointer',
                      width: '100%',
                      padding: '12px 12px',
                      borderRadius: 12,
                      marginBottom: 2,
                      background: conversationId === c.chatId
                        ? 'linear-gradient(135deg, rgba(217,70,239,0.16), rgba(124,58,237,0.12))'
                        : ('transparent'),
                      color: 'inherit',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => {
                      if (conversationId !== c.chatId) {
                        e.currentTarget.style.background = c.unread
                          ? 'rgba(217,70,239,0.12)'
                          : 'rgba(255,255,255,0.04)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (conversationId !== c.chatId) {
                        e.currentTarget.style.background = c.unread
                          ? 'rgba(217, 70, 239, 0.05)'
                          : 'transparent';
                      }
                    }}
                  >
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <MonoAvatar name={c.recipientName || '?'} size={42} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: '#fff',
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {c.recipientName}
                        </span>
                        <span style={{
                          fontSize: 11,
                          color: 'rgba(220,200,240,0.55)',
                          flexShrink: 0,
                          fontWeight: 500,
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {timeAgo(c.lastMessageAt)}
                        </span>
                      </div>
                      <p style={{
                        margin: 0,
                        marginTop: 2,
                        color: 'rgba(220,200,240,0.55)',
                        fontSize: 12.5,
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 400,
                      }}>
                        {c.lastMessage}
                      </p>
                    </div>
                    {c.unread && (
                      <span style={{
                        flexShrink: 0,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                        boxShadow: '0 0 6px rgba(217, 70, 239, 0.5)',
                      }} />
                    )}
                  </button>
                ))}

                {filtered.length === 0 && (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: 'rgba(220,200,240,0.4)',
                    fontSize: 13,
                  }}>
                    No threads match.
                  </div>
                )}

                {hasMore && (
                  <div style={{ padding: '12px 0', textAlign: 'center' }}>
                    <button
                      className="primary-button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      style={{ fontSize: 13, padding: '8px 20px' }}
                    >
                      {loadingMore ? 'loading...' : 'load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Right column: active conversation ── */}
        <div className="messages-convo-panel">
          {conversationId ? (
            <Conversation
              conversationId={conversationId}
              recipientId={activeConvo?.recipientId}
              recipientName={activeConvo?.recipientName}
              onBack={() => navigate('/messages')}
            />
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(220,200,240,0.4)',
              gap: 12,
              flex: 1,
            }}>
              <MessageCircle size={40} strokeWidth={1.2} />
              <p style={{ margin: 0, fontSize: 14 }}>select a conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
